import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  sourceSubsitesService,
  type CreateSourceSubsiteData,
  type UpdateSourceSubsiteData,
} from "../../services/admin/sourcesubsites.service";

interface ApiErrorWithStatus {
  response?: { status?: number };
}

const getCreateSubsiteErrorMessage = (error: unknown): string => {
  const status = (error as ApiErrorWithStatus)?.response?.status;
  return status === 409
    ? "This subsite already exists for the selected source. Select the existing subsite instead."
    : "We couldn't create the subsite. Check the details and try again.";
};

export function useSourceSubsites() {
  return useQuery({
    queryKey: ["admin", "source_subsites"],
    queryFn: sourceSubsitesService.getAll,
  });
}

export function useSourceSubsite(id: string) {
  return useQuery({
    queryKey: ["admin", "source_subsites", id],
    queryFn: () => sourceSubsitesService.getById(id),
    enabled: !!id,
  });
}

export function useSourceSubsitesBySourceId(sourceId: string) {
  return useQuery({
    queryKey: ["admin", "source_subsites", "by_source", sourceId],
    queryFn: () => sourceSubsitesService.getBySourceId(sourceId),
    enabled: !!sourceId,
  });
}

export function useCreateSourceSubsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSourceSubsiteData) =>
      sourceSubsitesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "source_subsites"] });
      queryClient.invalidateQueries({ queryKey: ["sources-with-subsites"] });
      notifications.show({
        title: "Success",
        message: "Source subsite created successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getCreateSubsiteErrorMessage(error),
        color: "red",
      });
    },
  });
}

export function useUpdateSourceSubsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSourceSubsiteData) =>
      sourceSubsitesService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "source_subsites"] });
      notifications.show({
        title: "Success",
        message: "Source subsite updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update source subsite",
        color: "red",
      });
    },
  });
}

export function useToggleSourceSubsiteActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      sourceSubsitesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "source_subsites"] });
      notifications.show({
        title: "Success",
        message: "Source subsite status updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update source subsite status",
        color: "red",
      });
    },
  });
}

export function useDeleteSourceSubsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sourceSubsitesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "source_subsites"] });
      notifications.show({
        title: "Success",
        message: "Source subsite deleted successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete source subsite",
        color: "red",
      });
    },
  });
}
